/**
 * MuxPosterBackdrop — friendly progressive "blur-up" backdrop for a Mux video.
 *
 * Why this exists: a single full-size Mux thumbnail set as a CSS background
 * makes the user stare at a flat black screen + spinner until the whole
 * ~30-80 KB WebP downloads (painful on 3G / Instagram in-app browser).
 *
 * Strategy (industry-standard blur-up):
 *   1. Tasteful dark gradient base   → never a raw black flash, paints instantly.
 *   2. LQIP (≈40 px, q30, ~1-2 KB)   → blurred via CSS, lands almost immediately,
 *                                       gives instant colour + composition context.
 *   3. Full poster (device-sized)    → fades in over the LQIP once decoded.
 *   4. onReady() fires when the full  → lets the parent retire its spinner as soon
 *      poster is painted                as there is a real frame on screen.
 *
 * The LQIP <link rel="preload"> is injected as soon as the playbackId is known so
 * the browser fetches it in parallel with the JS that mounts the player. No
 * crossOrigin is set (image.mux.com doesn't need it; setting it breaks the hint).
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { cn } from "@/lib/utils";

export interface MuxPosterBackdropProps {
  /** Mux playback ID. When empty/undefined only the gradient base renders. */
  playbackId?: string | null;
  /** Frame time (seconds) for the thumbnail. Default 0 (first frame). */
  time?: number;
  /** Mux image fit mode. "smartcrop" fills (reels); "preserve" letterboxes. */
  fitMode?: "smartcrop" | "preserve";
  /** Fired once the full-resolution poster has decoded and is visible. */
  onReady?: () => void;
  /** Hide everything (e.g. when the video is already playing) with a fade. */
  hidden?: boolean;
  className?: string;
}

/**
 * Round viewport width to a CDN-cache-friendly bucket (avoids per-px cache misses).
 * Cap at 720 for LCP: DualCTA traffic is mostly mobile/IG; 1080/1440 only delayed
 * LCP without visible gain on a full-bleed phone viewport.
 */
function posterWidthBucket(): number {
  if (typeof window === "undefined") return 720;
  const w = window.innerWidth * Math.min(window.devicePixelRatio || 1, 2);
  if (w <= 480) return 480;
  return 720;
}

export default function MuxPosterBackdrop({
  playbackId,
  time = 0,
  fitMode = "smartcrop",
  onReady,
  hidden = false,
  className,
}: MuxPosterBackdropProps) {
  const pid = playbackId?.trim() || "";

  const lqipUrl = useMemo(
    () =>
      pid
        ? `https://image.mux.com/${pid}/thumbnail.webp?time=${time}&width=40&quality=30&fit_mode=${fitMode}`
        : null,
    [pid, time, fitMode],
  );

  const fullUrl = useMemo(
    () =>
      pid
        // quality 65: enough for full-bleed mobile LCP without 80–120 KB WebPs.
        ? `https://image.mux.com/${pid}/thumbnail.webp?time=${time}&width=${posterWidthBucket()}&quality=65&fit_mode=${fitMode}`
        : null,
    [pid, time, fitMode],
  );

  const [fullLoaded, setFullLoaded] = useState(false);
  const [lqipLoaded, setLqipLoaded] = useState(false);
  const onReadyRef = useRef(onReady);
  onReadyRef.current = onReady;
  const readyFiredRef = useRef(false);

  const fireReadyOnce = () => {
    if (readyFiredRef.current) return;
    readyFiredRef.current = true;
    onReadyRef.current?.();
  };

  // Reset ready gate when playback id / time changes.
  useEffect(() => {
    readyFiredRef.current = false;
    setFullLoaded(false);
    setLqipLoaded(false);
  }, [pid, time, fitMode]);

  // Preload LQIP + full poster at high priority as soon as we know the ID.
  // Skip if index.html already preloaded the same URLs (duplicate hints are ok).
  useEffect(() => {
    if (typeof document === "undefined") return;
    const links: HTMLLinkElement[] = [];
    for (const href of [lqipUrl, fullUrl]) {
      if (!href) continue;
      if (document.querySelector(`link[rel="preload"][href="${href}"]`)) continue;
      const link = document.createElement("link");
      link.rel = "preload";
      link.as = "image";
      link.href = href;
      link.setAttribute("fetchpriority", "high");
      document.head.appendChild(link);
      links.push(link);
    }
    return () => {
      for (const link of links) link.remove();
    };
  }, [lqipUrl, fullUrl]);

  // Decode the full poster; this is the LCP candidate (large full-bleed image).
  useEffect(() => {
    if (!fullUrl) return;
    let cancelled = false;
    const img = new Image();
    img.src = fullUrl;
    const markReady = () => {
      if (cancelled) return;
      setFullLoaded(true);
      fireReadyOnce();
    };
    if (typeof img.decode === "function") {
      img.decode().then(markReady, markReady);
    } else {
      img.onload = markReady;
      img.onerror = markReady;
    }
    return () => {
      cancelled = true;
    };
  }, [fullUrl]);

  return (
    <div
      aria-hidden
      className={cn(
        "absolute inset-0 overflow-hidden transition-opacity duration-300 ease-out",
        hidden ? "opacity-0" : "opacity-100",
        className,
      )}
    >
      {/* 1. Instant base — a calm dark gradient, never a raw black flash. */}
      <div className="absolute inset-0 bg-gradient-to-br from-zinc-950 via-black to-purple-950/40" />

      {/* 2. LQIP — blurred, slightly over-scaled so blur edges never show. */}
      {lqipUrl && (
        <img
          src={lqipUrl}
          alt=""
          decoding="async"
          fetchPriority="high"
          onLoad={() => {
            setLqipLoaded(true);
            // Spinner can drop once colour composition is on screen.
            if (!fullLoaded) fireReadyOnce();
          }}
          className={cn(
            "absolute inset-0 h-full w-full object-cover scale-110 blur-2xl",
            lqipLoaded ? "opacity-100" : "opacity-0",
          )}
          style={{ willChange: "opacity" }}
        />
      )}

      {/* 3. Full poster — LCP element. High priority + no long fade delay. */}
      {fullUrl && (
        <img
          src={fullUrl}
          alt=""
          decoding="async"
          fetchPriority="high"
          className={cn(
            "absolute inset-0 h-full w-full object-cover transition-opacity duration-300 ease-out",
            fullLoaded ? "opacity-100" : "opacity-0",
          )}
        />
      )}
    </div>
  );
}
